import { CustomCollapseCard, CustomTable, useText, Text } from '@src/components';
import React, { useCallback, useEffect, useMemo } from 'react';
import '@src/styles/currentAccount/list.scss';
import { useDispatch, useSelector } from 'react-redux';
import { currentAccountList } from '@src/store/slice/invoiceSlice';
import dayjs from 'dayjs';
import { defaultDateFormat } from '@src/utils/keys';
import { getFileNameByLanguage, moneyFormat } from '@src/utils/utils';
import { Button, Dropdown, Menu } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import invoicesServices from '@src/services/invoices.services';
import download from 'js-file-download';
import { VouIdCode } from '@src/store/enums';

const getDefaultDateRange = () => ({
  startDate: dayjs().subtract(1, 'year').format('YYYY-MM-DD'),
  endDate: dayjs().format('YYYY-MM-DD'),
});

const formatDateCell = (text) => <span>{dayjs(text).format(defaultDateFormat)}</span>;

const formatMoneyCell = (text) => <span>{text ? `${moneyFormat(text, false)} TL` : '0'}</span>;

const headerContainerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  flexDirection: 'row',
  alignItems: 'center',
  paddingTop: 31,
  paddingLeft: 20,
  paddingBottom: 22,
  paddingRight: 24,
};

const countStyle = {
  fontFamily: 'Ubuntu',
  fontSize: 12,
  fontWeight: 'normal',
  fontStretch: 'normal',
  fontStyle: 'normal',
  lineHeight: 'normal',
  letterSpacing: 'normal',
  textAlign: 'left',
  color: '#6b7789',
};

const CurrentContent = () => {
  const dispatch = useDispatch();
  const { currentAccounts } = useSelector((state) => state?.invoice);
  const { selectedCustomer } = useSelector((state) => state?.customer);

  const loadCurrentAccountList = useCallback(() => {
    const { startDate, endDate } = getDefaultDateRange();
    const body = {
      customerId: selectedCustomer?.customerId,
      vouId: VouIdCode.Market,
      startDate,
      endDate,
    };
    dispatch(currentAccountList({ body }));
  }, [dispatch, selectedCustomer]);

  useEffect(() => {
    loadCurrentAccountList();
  }, [loadCurrentAccountList]);

  const downloadReport = useCallback(
    async (format) => {
      const { startDate, endDate } = getDefaultDateRange();
      const payload = {
        customerId: selectedCustomer?.customerId,
        vouId: VouIdCode.Market,
        startDate,
        endDate,
      };

      let data;
      if (format === 'pdf') {
        data = await invoicesServices.currentAccountGetPDFFile(payload);
      } else {
        data = await invoicesServices.currentAccountGetExcelFile(payload);
      }

      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      download(
        data,
        `${getFileNameByLanguage('cari-hesap-raporu', 'current-account-report')}-${dayjs().format('DD-MM-YYYY hh:mm')}.${ext}`,
      );
    },
    [selectedCustomer?.customerId],
  );

  const memoizedColumns = useMemo(
    () => [
      {
        title: <Text t="transactionDate2" />,
        dataIndex: 'transactionDate',
        key: 'transactionDate',
        align: 'left',
        width: 110,
        render: formatDateCell,
      },
      {
        title: <Text t="transactionNumber" />,
        dataIndex: 'documentNumber',
        key: 'documentNumber',
        width: 140,
        align: 'left',
      },
      {
        title: <Text t="transactionType2" />,
        dataIndex: 'transactionType',
        key: 'transactionType',
        width: 110,
        align: 'left',
      },
      {
        title: <Text t="description2" />,
        dataIndex: 'explanation',
        key: 'explanation',
        width: 270,
        height: 50,
        align: 'left',
      },
      {
        title: <Text t="debit" />,
        dataIndex: 'debit',
        key: 'debit',
        width: 120,
        height: 50,
        align: 'left',
        render: formatMoneyCell,
      },
      {
        title: <Text t="credit" />,
        dataIndex: 'credit',
        key: 'credit',
        width: 120,
        height: 50,
        align: 'left',
        render: formatMoneyCell,
      },
      {
        title: <Text t="balanceUpperCase" />,
        dataIndex: 'total',
        key: 'total',
        width: 90,
        align: 'left',
        render: formatMoneyCell,
      },
    ],
    [],
  );

  const menu = (
    <Menu>
      <Menu.Item onClick={() => downloadReport('xlsx')}>
        <Text t="downloadReportExcel" />{' '}
      </Menu.Item>
      <Menu.Item onClick={() => downloadReport('pdf')}>
        <Text t="downloadReportPdf" />
      </Menu.Item>
    </Menu>
  );

  return (
    <CustomCollapseCard className="current-account-list-card" cardTitle={<Text t="currentAccountReport" />}>
      <div style={headerContainerStyle}>
        <span style={countStyle}>
          <Text t="total" />
          {currentAccounts?.length} <Text t="record" />
        </span>
        <div style={{ width: 40, height: 40 }}>
          <Dropdown overlay={menu} trigger={['click']}>
            <Button shape="circle" icon={<MoreOutlined />} />
          </Dropdown>
        </div>
      </div>
      <CustomTable
        pagination={false}
        dataSource={currentAccounts}
        columns={memoizedColumns}
        className="current-account-table"
        rowKey={(record) => `current-account-${record?.rowId}`}
      />
    </CustomCollapseCard>
  );
};

export default CurrentContent;
